import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

export default function VectorAccuracy() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-vector-accuracy-proxy'],
    queryFn: async () => {
      // Proxy metric: distribution of accuracy_score in timing_metadata for recent assistant messages
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: msgs } = await supabase
        .from('chat_messages')
        .select('timing_metadata')
        .eq('role', 'assistant')
        .gte('created_at', since)
        .limit(5000);

      const scores = (msgs || [])
        .map((m: any) => Number(m?.timing_metadata?.accuracy_score))
        .filter((n) => Number.isFinite(n));

      const avg = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
      const below60 = scores.filter((s) => s < 60).length;
      const between60and80 = scores.filter((s) => s >= 60 && s < 80).length;
      const above80 = scores.filter((s) => s >= 80).length;

      return { count: scores.length, avg, below60, between60and80, above80 };
    }
  });

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Vector Accuracy (Proxy)</h1>
          <p className="text-muted-foreground">Heuristic proxy using accuracy_score in timing metadata.</p>
        </div>
        {error && <div className="text-red-600 text-sm">{String((error as any)?.message || error)}</div>}
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Samples</div>
            <div className="text-2xl font-semibold">{isLoading ? '…' : data?.count}</div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Average Score</div>
            <div className="text-2xl font-semibold">{isLoading ? '…' : Math.round((data?.avg || 0) * 10) / 10}</div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Below 60</div>
            <div className="text-2xl font-semibold">{isLoading ? '…' : data?.below60}</div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">60 - 80</div>
            <div className="text-2xl font-semibold">{isLoading ? '…' : data?.between60and80}</div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">80+</div>
            <div className="text-2xl font-semibold">{isLoading ? '…' : data?.above80}</div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}


